import CheckIcon from '@mui/icons-material/Check';
import { Box, Stack, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React from 'react';

export interface WorkflowStepConfig {
    label: string;
    sublabel: string;
    icon: React.ReactNode;
}

interface WorkflowProgressStepperProps {
    steps: WorkflowStepConfig[];
    activeStep: number;
    onStepClick?: (index: number) => void;
}

const useStyles = makeStyles((theme) => ({
    root: {
        padding: '24px 40px 16px',
    },
    stepContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        flex: 1,
    },
    stepContainerClickable: {
        cursor: 'pointer',
        '&:hover $circle': {
            transform: 'scale(1.1)',
            boxShadow: `0 0 0 4px ${theme.colors.cyan['100']}`,
        },
        '&:hover $sublabel, &:hover $label': {
            fontWeight: 700,
        },
    },
    circle: {
        width: 48,
        height: 48,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1,
        transition: 'all 300ms ease-in-out',
    },
    activeCircle: {
        backgroundColor: theme.colors.cyan['500'],
        color: '#fff',
        boxShadow: `0 0 0 4px ${theme.colors.cyan['100']}`,
    },
    completedCircle: {
        backgroundColor: theme.colors.cyan['500'],
        color: '#fff',
    },
    upcomingCircle: {
        backgroundColor: theme.colors.neutral['200'],
        color: theme.colors.neutral['500'],
    },
    connector: {
        height: 2,
        flex: 1,
        minWidth: 40,
        alignSelf: 'flex-start',
        marginTop: 23,
        backgroundColor: theme.colors.neutral['300'],
        transition: 'background-color 300ms ease-in-out',
    },
    connectorCompleted: {
        backgroundColor: theme.colors.cyan['500'],
    },
    label: {
        fontFamily: 'Inter',
        fontSize: '0.85rem',
        fontWeight: 600,
        marginTop: 8,
        textAlign: 'center',
        transition: 'color 300ms ease-in-out',
    },
    activeLabel: {
        color: theme.colors.cyan['500'],
    },
    completedLabel: {
        color: theme.colors.cyan['500'],
    },
    upcomingLabel: {
        color: theme.colors.neutral['400'],
    },
    sublabel: {
        fontFamily: 'Inter',
        fontSize: '0.75rem',
        fontWeight: 400,
        textAlign: 'center',
        color: theme.colors.neutral['500'],
    },
}));

const WorkflowProgressStepper: React.FC<WorkflowProgressStepperProps> = ({ steps, activeStep, onStepClick }) => {
    const classes = useStyles();

    const getCircleClass = (index: number) => {
        if (index < activeStep) return `${classes.circle} ${classes.completedCircle}`;
        if (index === activeStep) return `${classes.circle} ${classes.activeCircle}`;
        return `${classes.circle} ${classes.upcomingCircle}`;
    };

    const getLabelClass = (index: number) => {
        if (index < activeStep) return `${classes.label} ${classes.completedLabel}`;
        if (index === activeStep) return `${classes.label} ${classes.activeLabel}`;
        return `${classes.label} ${classes.upcomingLabel}`;
    };

    return (
        <Box className={classes.root}>
            <Stack direction="row" alignItems="flex-start" justifyContent="center">
                {steps.map((step, index) => (
                    <React.Fragment key={`workflow-step-${index}`}>
                        <Box className={`${classes.stepContainer} ${onStepClick ? classes.stepContainerClickable : ''}`}
                            onClick={() => onStepClick?.(index)}>
                            <Box className={getCircleClass(index)}>
                                {index < activeStep ? (
                                    <CheckIcon sx={{ width: 24, height: 24 }} />
                                ) : (
                                    step.icon
                                )}
                            </Box>
                            <Typography className={getLabelClass(index)}>
                                {step.label}
                            </Typography>
                            <Typography className={classes.sublabel}>
                                {step.sublabel}
                            </Typography>
                        </Box>
                        {index < steps.length - 1 && (
                            <Box className={`${classes.connector} ${index < activeStep ? classes.connectorCompleted : ''}`} />
                        )}
                    </React.Fragment>
                ))}
            </Stack>
        </Box>
    );
};

export default WorkflowProgressStepper;
